import { useMemo } from 'react';
import {
  isMacro,
  type MacroLookupEntry,
} from 'domains/reporter/RichTextEditor/plugins/macroPlaceholder/utils';
import { logger } from 'modules/logger';
import { usePicklistEditor } from './usePicklist';

const EMPTY_MACRO_ENTRIES: ReadonlyArray<MacroLookupEntry> = [];
const isStringOrNil = (value: unknown): value is Nullable<string> =>
  value == null || typeof value === 'string';

const toValidMacroEntry = (value: unknown): Nullable<MacroLookupEntry> => {
  if (typeof value !== 'object' || value == null) {
    return null;
  }

  const maybeEntry = value as {
    smid?: unknown;
    name?: unknown;
    nestedMacros?: unknown;
    text?: unknown;
  };

  if (typeof maybeEntry.smid !== 'string') {
    return null;
  }

  if (!isStringOrNil(maybeEntry.name)) {
    return null;
  }

  const maybeText = maybeEntry.text as MacroLookupEntry['text'];
  if (!(isStringOrNil(maybeText) || isMacro(maybeText))) {
    return null;
  }

  let nestedMacros: MacroLookupEntry['nestedMacros'];
  let droppedNestedCount = 0;
  if (maybeEntry.nestedMacros != null) {
    if (!Array.isArray(maybeEntry.nestedMacros)) {
      return null;
    }

    const sanitizedNestedEntries = maybeEntry.nestedMacros
      .map(toValidMacroEntry)
      .filter((entry): entry is MacroLookupEntry => entry != null);
    droppedNestedCount = maybeEntry.nestedMacros.length - sanitizedNestedEntries.length;
    nestedMacros = sanitizedNestedEntries;
  }

  if (droppedNestedCount > 0) {
    logger.warn('[useMacros] Dropped invalid nested macro entries during normalization.', {
      macroSmid: maybeEntry.smid,
      droppedNestedCount,
    });
  }

  return {
    smid: maybeEntry.smid,
    ...(maybeEntry.name == null ? {} : { name: maybeEntry.name }),
    ...(maybeText == null ? {} : { text: maybeText }),
    ...(nestedMacros == null ? {} : { nestedMacros }),
  };
};

export const toMacroEntries = (macros: unknown): ReadonlyArray<MacroLookupEntry> => {
  if (!Array.isArray(macros)) {
    return EMPTY_MACRO_ENTRIES;
  }

  const sanitizedEntries = macros
    .map(toValidMacroEntry)
    .filter((entry): entry is MacroLookupEntry => entry != null);
  const droppedCount = macros.length - sanitizedEntries.length;

  if (droppedCount > 0) {
    logger.warn('[useMacros] Dropped invalid macro entries during normalization.', {
      droppedCount,
      totalCount: macros.length,
    });
  }

  return sanitizedEntries.length > 0 ? sanitizedEntries : EMPTY_MACRO_ENTRIES;
};

/**
 * Normalize mixed macro sources from Picklist context into a stable lookup shape for editor consumers.
 * The upstream `Macros` union includes multiple GraphQL/local variants, so consumers should use this
 * hook instead of reading `usePicklistEditor().macros` directly.
 */
export const useMacros = (): ReadonlyArray<MacroLookupEntry> => {
  const { macros } = usePicklistEditor();
  return useMemo(() => toMacroEntries(macros), [macros]);
};
